import React, { useState, useEffect, useRef } from "react";
import { ReactSortable } from "react-sortablejs";
import RecentTransactions from "../../widgets/RecentTransactions/index.tsx";
import SpendingBreakdown from "../../widgets/SpendingBreakdown/index.tsx";
import ActiveSubscriptions from "../../widgets/ActiveSubscriptions/index.tsx";
import { useBalanceSummary } from "../../hooks/useBalanceSummary.tsx";
import { useExchangeRates } from "../../hooks/useExchangeRates.tsx";
import Modal from "../../components/Modal/index.tsx";
import { AddTransactionForm } from "../../components/AddTransaction/AddTransactionForm.tsx";
import { TransactionFormData } from "../../types/transaction.tsx";
import { useTransactions } from "../../hooks/useTransactions.tsx";
import { v4 as uuidv4 } from "uuid";
import { Calendar as CalendarIcon } from "lucide-react";
import dayjs from "dayjs";
import DateRangePicker from "../../components/DateRangePicker/index.tsx";

interface Widget {
  id: number;
  title: string;
  description: string;
  icon: string;
  component: React.ReactNode;
  header: boolean;
  // Responsive column spans for different breakpoints
  responsive: {
    xs: number; // Extra small screens (<640px)
    sm: number; // Small screens (640px+)
    md: number; // Medium screens (768px+)
    lg: number; // Large screens (1024px+)
  };
}

const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [widgetConfig, setWidgetConfig] = useState<Widget[]>([]);
  const BASE_CURRENCY = (() => {
    try {
      return localStorage.getItem("pref:baseCurrency") || "AED";
    } catch {
      return "AED";
    }
  })();
  const { addTransaction } = useTransactions();
  const [showModal, setShowModal] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const rangeWrapRef = useRef<HTMLDivElement | null>(null);
  const rangePopoverRef = useRef<HTMLDivElement | null>(null);

  const [balanceSummaryInBaseCurrency, setBalanceSummaryInBaseCurrency] =
    useState({
      balance: 0,
      expense: 0,
      income: 0,
      currency: BASE_CURRENCY,
    });

  const { balanceSummary } = useBalanceSummary();
  const { rates } = useExchangeRates(BASE_CURRENCY);

  useEffect(() => {
    const getBaseAmount = (amount: number, fromCurrency: string): number => {
      if (!rates || !rates[fromCurrency]) return 0;
      return (
        amount * (rates[BASE_CURRENCY.toLowerCase()] / rates[fromCurrency])
      );
    };

    let convertedBalanceSummary = {
      balance: getBaseAmount(balanceSummary.balance, balanceSummary.currency),
      expense: getBaseAmount(balanceSummary.expense, balanceSummary.currency),
      income: getBaseAmount(balanceSummary.income, balanceSummary.currency),
      currency: BASE_CURRENCY,
    };
    setBalanceSummaryInBaseCurrency(convertedBalanceSummary);
  }, [rates, balanceSummary, BASE_CURRENCY]);

  useEffect(() => {
    const saved = localStorage.getItem("dashboard:widgets");
    const savedRange = localStorage.getItem("dashboard:range");
    if (savedRange) {
      try {
        const r = JSON.parse(savedRange);
        setDateFrom(r?.from || "");
        setDateTo(r?.to || "");
      } catch {}
    }
    const defaultWidgets: Widget[] = [
      {
        id: 1,
        title: "Recent Transactions",
        description: "View your latest income, expenses, and transfers",
        icon: "receipt",
        component: <RecentTransactions />,
        header: true,
        responsive: {
          xs: 1, // Full width on extra small
          sm: 1, // Full width on small
          md: 1, // Half width on medium
          lg: 1, // Half width on large
        },
      },
      {
        id: 2,
        title: "Spending Breakdown",
        description: "Categorized view of all expenses",
        icon: "bar_chart",
        component: <SpendingBreakdown />,
        header: true,
        responsive: {
          xs: 1, // Full width on extra small
          sm: 1, // Full width on small
          md: 1, // Wider chart on md+
          lg: 1, // Wider chart on lg
        },
      },
      {
        id: 3,
        title: "Current Active Subscriptions",
        description: "Monitor your monthly and yearly subscription costs",
        icon: "bar_chart",
        component: <ActiveSubscriptions />,
        header: true,
        responsive: {
          xs: 1, // Full width on extra small
          sm: 1, // Full width on small
          md: 1, // Half width on medium
          lg: 1, // Half width on large
        },
      },
      // {
      //   id: 4,
      //   title: "Financial Goals",
      //   description: "Track savings progress toward your active goals",
      //   icon: "savings",
      //   component: <FinancialGoals />,
      //   header: true,
      //   responsive: {
      //     xs: 1, // Full width on extra small
      //     sm: 1, // Full width on small
      //     md: 1, // Half width on medium
      //     lg: 1, // Half width on large
      //   },
      // },
      // {
      //   id: 6,
      //   title: "Budget Utilization",
      //   description: "Compare planned budgets vs actual spend",
      //   icon: "pie_chart",
      //   component: <BudgetUtilization />,
      //   header: true,
      //   responsive: {
      //     xs: 1, // Full width on extra small
      //     sm: 1, // Full width on small
      //     md: 1, // Give more room to charts
      //     lg: 1, // Give more room to charts
      //   },
      // },
      // {
      //   id: 7,
      //   title: "Debt & Loan Summary",
      //   description: "View loan balances and repayments at a glance",
      //   icon: "credit_card",
      //   component: <DebtLoanSummary />,
      //   header: true,
      //   responsive: {
      //     xs: 1, // Full width on extra small
      //     sm: 1, // Full width on small
      //     md: 1, // Half width on medium
      //     lg: 1, // 1/3 width on large
      //   },
      // },
      // {
      //   id: 9,
      //   title: "Assets",
      //   description: "Quick snapshot of your finances",
      //   icon: "account_balance",
      //   component: <AssetsDebtsSavings />,
      //   header: true,
      //   responsive: {
      //     xs: 1,
      //     sm: 1,
      //     md: 1,
      //     lg: 1,
      //   },
      // },
      // {
      //   id: 8,
      //   title: "Cash Flow Overview",
      //   description: "Monthly income vs expense trends",
      //   icon: "show_chart",
      //   component: <CashFlowOverview />,
      //   header: true,
      //   responsive: {
      //     xs: 2, // Full width on extra small
      //     sm: 2, // Full width on small
      //     md: 2, // Full width on medium
      //     lg: 2, // 2/3 width on large
      //   },
      // },
    ];
    // If we have a saved order, rehydrate using default widgets
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // New format: array of widget ids in order
        if (
          Array.isArray(parsed) &&
          parsed.every((v: any) => typeof v === "number")
        ) {
          const byId = new Map(defaultWidgets.map((w) => [w.id, w] as const));
          const ordered = parsed
            .map((id: number) => byId.get(id))
            .filter(Boolean) as Widget[];
          // Append any widgets not present in saved order (in case of new widgets)
          const missing = defaultWidgets.filter((w) => !parsed.includes(w.id));
          setWidgetConfig([...ordered, ...missing]);
          return;
        }
        // Old format (incorrect): array of widget objects including React elements
        // Fall back to defaults to avoid rendering serialized element objects
      } catch {}
    }
    setWidgetConfig(defaultWidgets);
  }, []);

  // Ensure a valid default range on first load if none saved
  useEffect(() => {
    if (!dateFrom || !dateTo) {
      const today = dayjs().startOf("day");
      const from =
        kpiPeriod === "7d"
          ? today.subtract(6, "day")
          : kpiPeriod === "30d"
          ? today.subtract(29, "day")
          : today.startOf("month");
      const fromStr = from.format("YYYY-MM-DD");
      const toStr = today.format("YYYY-MM-DD");
      setDateFrom(fromStr);
      setDateTo(toStr);
      try {
        localStorage.setItem(
          "dashboard:range",
          JSON.stringify({ from: fromStr, to: toStr })
        );
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close range popover on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent | TouchEvent) {
      if (!rangeOpen) return;
      const target = e.target as Node | null;
      if (!target) return;
      const inPopover =
        rangePopoverRef.current && rangePopoverRef.current.contains(target);
      const inTrigger =
        rangeWrapRef.current && rangeWrapRef.current.contains(target);
      if (inPopover || inTrigger) return;
      setRangeOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [rangeOpen]);

  // Persist widget layout on change: store only widget ids (not React elements)
  useEffect(() => {
    if (widgetConfig && widgetConfig.length) {
      try {
        const orderIds = widgetConfig.map((w) => w.id);
        localStorage.setItem("dashboard:widgets", JSON.stringify(orderIds));
      } catch {}
    }
  }, [widgetConfig]);

  const handleAddTransaction = (data: TransactionFormData) => {
    addTransaction({
      ...data,
      id: uuidv4(),
      status: "completed",
      category: data.category ?? "miscellaneous",
    });
    setShowModal(false);
  };

  // Create responsive class string using static mappings so Tailwind sees all classes
  const SPAN: Record<number, string> = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
  };
  const SM_SPAN: Record<number, string> = {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
  };
  const MD_SPAN: Record<number, string> = {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
  };
  const LG_SPAN: Record<number, string> = {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
  };
  const getResponsiveColSpan = (responsive: Widget["responsive"]) => {
    return [
      SPAN[responsive.xs] || SPAN[1],
      SM_SPAN[responsive.sm] || SM_SPAN[1],
      MD_SPAN[responsive.md] || MD_SPAN[1],
      LG_SPAN[responsive.lg] || LG_SPAN[1],
    ].join(" ");
  };

  const formatAmount = (value: number) => {
    const num = Number.isFinite(value) ? value : 0;
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  // KPI period toggle and sparkline series
  const [kpiPeriod, setKpiPeriod] = useState<"7d" | "30d" | "month">("month");
  const applyPresetRange = (id: "7d" | "30d" | "month") => {
    const today = dayjs().startOf("day");
    let start = today.startOf("month");
    let end = today;
    if (id === "7d") {
      start = today.subtract(6, "day");
    } else if (id === "30d") {
      start = today.subtract(29, "day");
    } else {
      start = today.startOf("month");
    }
    const fromStr = start.format("YYYY-MM-DD");
    const toStr = end.format("YYYY-MM-DD");
    setDateFrom(fromStr);
    setDateTo(toStr);
    try {
      localStorage.setItem(
        "dashboard:range",
        JSON.stringify({ from: fromStr, to: toStr })
      );
    } catch {}
  };

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        <div className="text-white flex items-start justify-between mb-3 gap-5 flex-wrap border-b border-white/15 pb-3">
          <div>
            <h3 className="heading-2">
              Welcome, {user.first_name || "Guest"}!
            </h3>
            <p className="sub-heading-2">
              Take control of your financial well-being with smart insights and
              easy tracking.
            </p>
          </div>
          {/* Calendar quick link removed; available via SideNav */}
        </div>

        {/* KPI period toggle + date range */}
        <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { id: "7d", label: "7d" },
              { id: "30d", label: "30d" },
              { id: "month", label: "This Month" },
            ].map((p) => (
              <button
                key={p.id}
                className={`btn-ghost px-3 py-1`}
                onClick={() => {
                  setKpiPeriod(p.id as any);
                  applyPresetRange(p.id as any);
                }}
                type="button"
              >
                {p.label}
              </button>
            ))}
            <div className="relative" ref={rangeWrapRef}>
              <button
                type="button"
                className="btn-ghost inline-flex items-center gap-2 px-3 py-1"
                onClick={() => setRangeOpen((v) => !v)}
                title="Select date range"
              >
                <CalendarIcon className="h-4 w-4" />
                <span className="text-md">
                  {dateFrom && dateTo
                    ? `${dayjs(dateFrom).format("DD MMM YYYY")} – ${dayjs(
                        dateTo
                      ).format("DD MMM YYYY")}`
                    : "Select range"}
                </span>
              </button>
              {rangeOpen && (
                <div
                  ref={rangePopoverRef}
                  className="absolute z-20 mt-2 right-0 w-[300px] card-glass border border-white/10 rounded-lg p-3"
                >
                  <DateRangePicker
                    from={dateFrom}
                    to={dateTo}
                    initialMonth={dateFrom}
                    onApply={(f, t) => {
                      setDateFrom(f);
                      setDateTo(t);
                      try {
                        localStorage.setItem(
                          "dashboard:range",
                          JSON.stringify({ from: f, to: t })
                        );
                      } catch {}
                      setRangeOpen(false);
                    }}
                    onCancel={() => setRangeOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards - FIXED RESPONSIVE GRID */}
        <div className="text-white grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div
            className="flex flex-col h-28 border border-dashed border-white/10 bg-white/5 backdrop-blur py-5 px-6 gap-2 justify-between"
            role="region"
            aria-label="Expense summary"
          >
            <div className="widget-title">Total Expense</div>
            <div className="text-3xl font-semibold" aria-live="polite">
              {BASE_CURRENCY}{" "}
              {formatAmount(balanceSummaryInBaseCurrency?.expense)}
            </div>
          </div>

          <div
            className="flex flex-col h-28 border  border-dashed border-white/10 bg-white/5 backdrop-blur py-5 px-6 gap-2 justify-between"
            role="region"
            aria-label="Savings summary"
          >
            <div className="widget-title">Savings</div>
            <div className="text-3xl font-semibold" aria-live="polite">
              {BASE_CURRENCY} {formatAmount(0)}
            </div>
          </div>

          <div className="flex flex-col h-28 border  border-dashed border-white/10 bg-white/5 backdrop-blur py-5 px-6 gap-2 justify-between">
            <div className="widget-title">Upcoming Bills (Next 30 days)</div>
            <div className="text-3xl font-semibold">
              {BASE_CURRENCY} {formatAmount(0)}
            </div>
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Add Transaction"
        >
          <AddTransactionForm
            onSubmit={handleAddTransaction}
            onError={() => {}}
          />
        </Modal>

        {/* Responsive Grid Layout */}
        <ReactSortable
          list={widgetConfig}
          setList={(list) => {
            setWidgetConfig(list as any);
          }}
          animation={200}
          handle=".drag-handle"
          ghostClass="widget-ghost"
          chosenClass="widget-chosen"
          dragClass="widget-drag"
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 items-start"
        >
          {widgetConfig.map((widget, index) => (
            <div
              className={`${
                getResponsiveColSpan(widget.responsive) +
                " widget h-full min-h-[360px] md:min-h-[420px] bg-white/5 backdrop-blur"
              }`}
            >
              {widget.header && (
                <div className="widget-header">
                  <div>
                    <div className="widget-title">{widget.title}</div>
                    <div className="widget-subtitle">{widget.description}</div>
                  </div>
                </div>
              )}
              <div className="widget-content">{widget.component}</div>
            </div>
          ))}
        </ReactSortable>
      </div>
    </section>
  );
};

export default Dashboard;
